/**
 * PublicBadgePage — /badge/:token
 * Rendered when someone clicks a "Verified by Simonara" badge. Shows the
 * verification proof + a link back to Simonara. This is the trust-loop
 * payoff — the badge only means something if it lands somewhere real.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, BadgeCheck, ArrowRight, AlertCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function PublicBadgePage() {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, error: "", data: null });

    useEffect(() => {
        let mounted = true;
        if (!token) {
            setState({ loading: false, error: "Missing token.", data: null });
            return;
        }
        api.get(`/badge/${encodeURIComponent(token)}`)
            .then((r) => { if (mounted) setState({ loading: false, error: "", data: r.data }); })
            .catch((err) => {
                if (!mounted) return;
                const msg = err?.response?.status === 410
                    ? "This badge has been revoked or the underlying hire is no longer active."
                    : (err?.response?.data?.detail || "This badge isn't valid.");
                setState({ loading: false, error: msg, data: null });
            });
        return () => { mounted = false; };
    }, [token]);

    return (
        <div className="container-app py-16" data-testid="public-badge-page">
            <Helmet>
                <title>Verified by Simonara · Trust badge</title>
                <meta name="description" content="A Simonara Talent Solutions verification proof — issued only to talent hired through the platform." />
                <meta name="robots" content="noindex" />
            </Helmet>
            <div className="max-w-2xl mx-auto">
                {state.loading && (
                    <div className="py-12 flex justify-center" data-testid="public-badge-loading">
                        <Loader2 className="animate-spin text-emerald-500" />
                    </div>
                )}
                {!state.loading && state.error && (
                    <div className="card-soft p-8 border-red-500/40 bg-red-500/5" data-testid="public-badge-error">
                        <AlertCircle size={22} className="text-red-500" />
                        <h1 className="mt-3 font-heading font-semibold text-2xl text-foreground">Badge unavailable</h1>
                        <p className="mt-2 text-sm text-muted-foreground">{state.error}</p>
                        <div className="mt-5 flex flex-wrap gap-3">
                            <Link to="/" className="btn-outline text-sm" data-testid="public-badge-home">Go to Simonara</Link>
                            <Link to="/trust-center" className="text-sm underline text-muted-foreground hover:text-foreground">
                                Learn how badges are issued
                            </Link>
                        </div>
                    </div>
                )}
                {!state.loading && state.data && (
                    <div className="card-soft p-8 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-background to-sky-500/5" data-testid="public-badge-valid">
                        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                            <BadgeCheck size={12} /> Verified
                        </div>
                        <h1 className="mt-4 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground" data-testid="public-badge-name">
                            {state.data.display_name || "This talent"}
                        </h1>
                        <p className="mt-3 text-base text-muted-foreground">
                            Was hired through <b className="text-foreground">Simonara Talent Solutions</b> — the AI-powered talent platform where every profile is verified before an employer ever sees it.
                        </p>
                        <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div className="rounded-lg border border-border bg-muted/30 p-3">
                                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Issued by</dt>
                                <dd className="mt-0.5 text-foreground font-medium">{state.data.issuer}</dd>
                            </div>
                            <div className="rounded-lg border border-border bg-muted/30 p-3">
                                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Verified on</dt>
                                <dd className="mt-0.5 text-foreground font-medium">{state.data.verified_at ? new Date(state.data.verified_at).toLocaleDateString() : "—"}</dd>
                            </div>
                        </dl>
                        <div className="mt-6 rounded-xl border border-border bg-card p-4 flex justify-center">
                            <img src={state.data.assets?.svg} alt={`Verified by Simonara — ${state.data.display_name}`} className="max-w-full" style={{ maxWidth: 360 }} />
                        </div>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/employers" className="btn-green inline-flex items-center gap-2" data-testid="public-badge-cta-employers">
                                Hire verified talent <ArrowRight size={14} />
                            </Link>
                            <Link to="/trust-center" className="btn-outline inline-flex items-center gap-2" data-testid="public-badge-cta-trust">
                                <ShieldCheck size={14} /> How verification works
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
